import { AxiosError } from 'axios';
import {
  EarlyPickupReasonCode,
  EarlyPickupStatus,
} from '../types';
import { parseBackendDate } from './dateUtils';

export interface EarlyPickupFormState {
  pickupDate: string;
  // Held as HH:mm while editing; the API's HH:mm:ss is built at submit time.
  pickupTime: string;
  reasonCode: EarlyPickupReasonCode | '';
  reasonText: string;
  collectorContactId: string;
  notes: string;
}

export const REASON_OPTIONS: { code: EarlyPickupReasonCode; label: string }[] = [
  { code: 'MEDICAL', label: 'Medical appointment' },
  { code: 'FAMILY', label: 'Family commitment' },
  { code: 'TRAVEL', label: 'Travel' },
  { code: 'EMERGENCY', label: 'Emergency' },
  { code: 'OTHER', label: 'Other' },
];

export const MAX_TEXT_LENGTH = 500;

export type StatusTone = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'checkedOut';

// Every status carries its own words and icon: colour alone must never be what tells a
// parent what happened. `meaning` exists so "Approved" can never be read as "collected" —
// only CHECKED_OUT says the child has actually left.
export const STATUS_META: Record<
  EarlyPickupStatus,
  { label: string; icon: string; tone: StatusTone; meaning: string }
> = {
  PENDING: {
    label: 'Pending',
    icon: 'clock',
    tone: 'pending',
    meaning: 'Waiting for the school to review your request.',
  },
  APPROVED: {
    label: 'Approved',
    icon: 'check-circle',
    tone: 'approved',
    meaning: 'Approved — your child is still in school, waiting to be collected.',
  },
  REJECTED: {
    label: 'Rejected',
    icon: 'x-circle',
    tone: 'rejected',
    meaning: 'The school could not approve this request.',
  },
  CANCELLED: {
    label: 'Cancelled',
    icon: 'slash',
    tone: 'cancelled',
    meaning: 'This request was cancelled and is no longer active.',
  },
  CHECKED_OUT: {
    label: 'Checked out',
    icon: 'log-out',
    tone: 'checkedOut',
    meaning: 'Your child has been collected and has left the school.',
  },
};

export const STATUS_FILTERS: { value: EarlyPickupStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'CHECKED_OUT', label: 'Checked out' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function todayDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

export function formatPickupDate(dateStr: string): { date: string; day: string } {
  const [y, m, d] = (dateStr ?? '').slice(0, 10).split('-');
  if (!y || !m || !d) return { date: dateStr ?? '', day: '' };
  const parsed = new Date(Number(y), Number(m) - 1, Number(d));
  return {
    date: `${d} ${MONTHS[Number(m) - 1]} ${y}`,
    day: WEEKDAYS[parsed.getDay()],
  };
}

// Accepts both the API's HH:mm:ss and the form's HH:mm.
export function formatPickupTime(timeStr?: string): string {
  if (!timeStr) return '';
  const [hourPart, minutePart] = timeStr.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return timeStr;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`;
}

export function formatTimestamp(isoStr?: string): string {
  if (!isoStr) return '';
  // parseBackendDate, not new Date: approvedAt/rejectedAt/submittedAt come back
  // with no timezone marker, so a plain parse read them as local and rendered
  // every "Requested at…" shifted by the device's UTC offset (see dateUtils).
  const parsed = parseBackendDate(isoStr);
  if (!parsed) return isoStr;
  const { date } = formatPickupDate(
    `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
      parsed.getDate(),
    ).padStart(2, '0')}`,
  );
  return `${date}, ${formatPickupTime(
    `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`,
  )}`;
}

// Returns HH:mm when the input is a recognisable time, otherwise null.
export function normalizeTimeInput(raw: string): string | null {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(raw.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function toApiTime(hhmm: string): string {
  return `${hhmm}:00`;
}

export function validateEarlyPickupForm(
  form: EarlyPickupFormState,
  hasStudent: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!hasStudent) {
    errors.student = 'Select which child this request is for';
  }
  if (!form.pickupDate.trim()) {
    errors.pickupDate = 'Pickup date is required';
  }

  const normalizedTime = normalizeTimeInput(form.pickupTime);
  if (!form.pickupTime.trim()) {
    errors.pickupTime = 'Pickup time is required';
  } else if (!normalizedTime) {
    errors.pickupTime = 'Enter the time as HH:mm, for example 13:45';
  }

  if (!form.reasonCode) {
    errors.reasonCode = 'Reason is required';
  }
  if (form.reasonCode === 'OTHER' && !form.reasonText.trim()) {
    errors.reasonText = 'Tell the school the reason for the early pickup';
  }
  if (form.reasonText.length > MAX_TEXT_LENGTH) {
    errors.reasonText = `Reason cannot exceed ${MAX_TEXT_LENGTH} characters`;
  }
  if (!form.collectorContactId) {
    errors.collectorContactId = 'Choose who will collect your child';
  }
  if (form.notes.length > MAX_TEXT_LENGTH) {
    errors.notes = `Notes cannot exceed ${MAX_TEXT_LENGTH} characters`;
  }

  // A pickup time that has already passed today would be submitted only to be
  // refused by the office, so it is caught before the parent waits on a round trip.
  if (!errors.pickupTime && normalizedTime && form.pickupDate === todayDateString()) {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [hour, minute] = normalizedTime.split(':').map(Number);
    if (hour * 60 + minute <= currentMinutes) {
      errors.pickupTime = 'Pickup time has already passed today — choose a later time';
    }
  }

  return errors;
}

interface ApiErrorBody {
  message?: string;
  detail?: string;
  title?: string;
  error?: string;
  errors?: Record<string, string[] | string>;
}

// The backend writes its 400/409 bodies for parents to read, so they are surfaced
// verbatim instead of being flattened into a generic "something went wrong".
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as AxiosError<ApiErrorBody | string>)?.response;
  if (!response) {
    return 'Could not reach the school. Check your connection and try again.';
  }

  const data = response.data;
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === 'object') {
    const direct = data.message ?? data.detail ?? data.error ?? data.title;
    if (direct && direct.trim()) {
      return direct.trim();
    }
    if (data.errors) {
      const flattened = Object.values(data.errors)
        .flatMap(value => (Array.isArray(value) ? value : [value]))
        .filter(Boolean);
      if (flattened.length > 0) {
        return flattened.join('\n');
      }
    }
  }

  return fallback;
}
